import { Fragment, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { hapticLight } from '@/lib/haptics'
import { NotebookText, Pencil, Trash2 } from '@/components/icons'
import { Card } from './ui/card'
import { SectionTitle } from './ui/section-title'
import { MenuItem } from './ui/menu-item'
import { SwipeableRow } from './ui/swipeable-row'
import { EmptyStateCard } from './EmptyStateCard'
import { NoteDrawer, type NoteParent } from './NoteDrawer'
import { ViewNoteDrawer } from './ViewNoteDrawer'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'

type Note = { id: string; title: string | null; body: string }

interface DirectDeleteCallbacks {
  onSuccess: () => void
  onError: () => void
  onSettled: () => void
}

interface NotesSectionProps {
  notes: Note[] | undefined
  parent: NoteParent
  /** Section title, e.g. "My Notes" on the home screen. */
  title?: string
  onDirectDelete?: (id: string, cbs: DirectDeleteCallbacks) => void
  onDeleteRequest?: (id: string, title: string) => void
  readOnly?: boolean
  collapseAfter?: number
  /**
   * Controlled view-drawer state. When `onViewNoteIdChange` is provided, the
   * note being viewed is driven by `viewNoteId` (e.g. from a URL search param)
   * instead of internal state.
   */
  viewNoteId?: string | null
  onViewNoteIdChange?: (id: string | null) => void
}

export function NotesSection({
  notes,
  parent,
  title = 'Notes',
  onDirectDelete,
  onDeleteRequest,
  readOnly = false,
  collapseAfter,
  viewNoteId,
  onViewNoteIdChange,
}: NotesSectionProps) {
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)

  // The rendered note id is kept on close (only the open flag flips) so the
  // view drawer can play its exit animation before the content disappears.
  const [renderNoteId, setRenderNoteId] = useState<string | null>(
    viewNoteId ?? null,
  )
  const [isViewNoteOpen, setIsViewNoteOpen] = useState(!!viewNoteId)
  const isControlled = !!onViewNoteIdChange

  // Controlled mode (e.g. /home?note= deep link): adopt the id when it
  // appears; close with animation if the URL param is cleared externally.
  useEffect(() => {
    if (!isControlled) return
    if (viewNoteId) {
      setRenderNoteId(viewNoteId)
      setIsViewNoteOpen(true)
    } else {
      setIsViewNoteOpen(false)
    }
  }, [viewNoteId, isControlled])
  const [openRowId, setOpenRowId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const hasNotes = notes && notes.length > 0
  const isCollapsed =
    typeof collapseAfter === 'number' &&
    !!notes &&
    notes.length > collapseAfter &&
    !showAll
  const visibleNotes = isCollapsed ? notes!.slice(0, collapseAfter) : notes

  const openAdd = () => {
    setSelectedNoteId(null)
    setIsEditDrawerOpen(true)
  }

  const openView = (id: string) => {
    hapticLight()
    setRenderNoteId(id)
    setIsViewNoteOpen(true)
    onViewNoteIdChange?.(id)
  }

  const deleteNote = (id: string) => {
    const note = notes?.find((n) => n.id === id)
    if (onDeleteRequest) {
      onDeleteRequest(id, note?.title || note?.body || 'this note')
      return
    }
    if (!onDirectDelete) return
    setConfirmDeleteId(id)
  }

  const confirmDelete = () => {
    if (!confirmDeleteId || !onDirectDelete) return
    const id = confirmDeleteId
    setDeletingId(id)
    onDirectDelete(id, {
      onSuccess: () => {
        setOpenRowId(null)
        setConfirmDeleteId(null)
      },
      onError: () => toast.error('Failed to delete note'),
      onSettled: () => setDeletingId(null),
    })
  }

  const confirmNote = confirmDeleteId
    ? notes?.find((n) => n.id === confirmDeleteId)
    : undefined
  const confirmLabel =
    confirmNote?.title || confirmNote?.body || 'this note'

  const handleSwipeDelete = (note: Note) => {
    deleteNote(note.id)
    setOpenRowId(null)
  }

  const handleSwipeEdit = (note: Note) => {
    setOpenRowId(null)
    setSelectedNoteId(note.id)
    setIsEditDrawerOpen(true)
  }

  return (
    <div className="flex flex-col w-full">
      <SectionTitle
        label={title}
        count={notes?.length}
        icon={<NotebookText className="w-5 h-5 text-content-brand-bold" />}
        onAdd={readOnly ? undefined : openAdd}
      />

      {hasNotes ? (
        <>
          <Card className="flex flex-col gap-2 overflow-hidden p-3">
            {visibleNotes!.map((note, index) => (
              <Fragment key={note.id}>
                {index > 0 && <hr className="border-border-L1" />}
                <SwipeableRow
                  isOpen={openRowId === note.id}
                  onOpenChange={(o) => setOpenRowId(o ? note.id : null)}
                  onDelete={() => handleSwipeDelete(note)}
                  onEdit={readOnly ? undefined : () => handleSwipeEdit(note)}
                  actionLabel="Delete"
                  variant="icon"
                  actionIcon={<Trash2 />}
                  editIcon={<Pencil />}
                  pending={deletingId === note.id}
                  pendingLabel="Deleting..."
                  disabled={readOnly}
                >
                  <MenuItem
                    size="large"
                    onClick={() => openView(note.id)}
                    className="px-1"
                  >
                    <span className="font-bold truncate w-full">
                      {note.title || note.body}
                    </span>
                  </MenuItem>
                </SwipeableRow>
              </Fragment>
            ))}
          </Card>
          {typeof collapseAfter === 'number' &&
            !!notes &&
            notes.length > collapseAfter && (
              <button
                onClick={() => setShowAll((v) => !v)}
                className="pt-2 text-xs font-semibold text-content-brand-bold self-start"
              >
                {showAll ? 'Show Less' : 'Show All Notes'}
              </button>
            )}
        </>
      ) : (
        <EmptyStateCard
          label={readOnly ? 'No notes yet' : 'Add a note'}
          onAdd={readOnly ? undefined : openAdd}
        />
      )}

      <ViewNoteDrawer
        open={isViewNoteOpen}
        onOpenChange={(o) => {
          if (!o) {
            setIsViewNoteOpen(false)
            onViewNoteIdChange?.(null)
          }
        }}
        parent={parent}
        noteId={renderNoteId}
      />

      <NoteDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => {
          setIsEditDrawerOpen(false)
          setSelectedNoteId(null)
        }}
        parent={parent}
        noteId={selectedNoteId}
        readOnly={readOnly}
      />

      <AlertDialog
        open={confirmDeleteId !== null}
        onOpenChange={(o) => {
          if (!o && !deletingId) setConfirmDeleteId(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{confirmLabel}"? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingId}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={!!deletingId}
              className="bg-content-negative-bold hover:bg-content-negative-bold/90"
            >
              {deletingId ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
